import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { boardService } from '../../../../lib/api/boards';

interface ShareJournalModalProps {
    isOpen: boolean;
    onClose: () => void;
    journal: {
        id: string;
        name: string;
    } | null;
}

import { useConfirmDialog } from '../../../../components/ConfirmDialog';
import { useAlertStore } from '../../../../stores/alertStore';

interface ShareItem {
    id?: string;
    email: string;
    permission: 'read' | 'readwrite';
    isNew?: boolean;
    error?: boolean;
}

export const ShareJournalModal: React.FC<ShareJournalModalProps> = ({ isOpen, onClose, journal }) => {
    const { t } = useTranslation('common');
    const { showDialog } = useConfirmDialog();
    const { showAlert } = useAlertStore();
    const [shares, setShares] = useState<ShareItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Add ref to track newly added rows for auto-focus
    const lastInputRef = React.useRef<HTMLInputElement>(null);
    const [shouldFocusLast, setShouldFocusLast] = useState(false);

    useEffect(() => {
        if (shouldFocusLast && lastInputRef.current) {
            lastInputRef.current.focus();
            setShouldFocusLast(false);
        }
    }, [shouldFocusLast, shares]);

    useEffect(() => {
        if (isOpen && journal) {
            fetchShares();
        } else {
            setShares([]);
        }
    }, [isOpen, journal]);

    const fetchShares = async () => {
        if (!journal) return;
        setIsLoading(true);
        try {
            const data = await boardService.getShares(journal.id);
            const mappedShares = data.map((s: any) => ({
                id: s.id,
                email: s.email || '',
                permission: s.permission as 'read' | 'readwrite'
            }));

            // Always append one empty row for new entry
            setShares([...mappedShares, { email: '', permission: 'read', isNew: true }]);
            // We want to focus this new row
            setShouldFocusLast(true);
        } catch (err: any) {
            console.error('Failed to fetch shares:', err);
            // Fallback to one empty row on error if needed
            if (shares.length === 0) {
                setShares([{ email: '', permission: 'read', isNew: true }]);
                setShouldFocusLast(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddRow = () => {
        setShares([...shares, { email: '', permission: 'read', isNew: true }]);
        setShouldFocusLast(true);
    };

    const removeShare = (index: number) => {
        const updated = [...shares];
        updated.splice(index, 1);
        setShares(updated);
    };

    const handleRemoveRow = (index: number) => {
        const share = shares[index];

        // If it's a saved share (has ID and no isNew flag), ask for confirmation
        if (share.id && !share.isNew) {
            showDialog({
                title: t('sharing.removeShareTitle', 'Remove Access?') as string,
                message: t('sharing.removeShareMessage', 'Are you sure you want to remove access for {{email}}? This action cannot be undone.', { email: share.email }),
                confirmText: t('common.remove', 'Remove') as string,
                onConfirm: () => removeShare(index)
            });
        } else {
            // If new/unsaved, just remove
            removeShare(index);
        }
    };

    const handleUpdateRow = (index: number, field: keyof ShareItem, value: any) => {
        const updated = [...shares];
        updated[index] = { ...updated[index], [field]: value, error: false };
        setShares(updated);
    };

    const handleSave = async () => {
        if (!journal) return;
        setIsSaving(true);

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const seenEmails = new Set<string>();
        let hasError = false;

        const validatedShares = shares.map(share => {
            const email = share.email.trim();
            // Reset error initially
            let isError = false;

            // Only validate non-empty emails
            if (email) {
                if (!emailRegex.test(email)) {
                    isError = true;
                } else {
                    const lowerEmail = email.toLowerCase();
                    if (seenEmails.has(lowerEmail)) {
                        isError = true;
                    } else {
                        seenEmails.add(lowerEmail);
                    }
                }
            }

            if (isError) hasError = true;

            return { ...share, error: isError };
        });

        setShares(validatedShares);

        if (hasError) {
            setIsSaving(false);
            return;
        }

        // Filter out empty rows for submission
        const validShares = validatedShares.filter(s => s.email.trim());

        try {
            await boardService.updateJournalShares(journal.id, validShares);
            showAlert({ 
                type: 'success', 
                message: t('sharing.updateSuccess', 'Journal shares updated successfully') as string 
            });
            onClose();
        } catch (err: any) {
            console.error('Failed to save shares:', err);
            showAlert({ 
                type: 'error', 
                message: t('sharing.updateError', 'Failed to update journal shares') as string 
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        onClose();
    };

    if (!journal) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
                        onClick={handleClose}
                    />
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl w-full max-w-lg pointer-events-auto overflow-hidden flex flex-col max-h-[90vh]"
                        >
                            {/* Header */}
                            <div className="p-6 pb-2 flex justify-between items-center bg-white dark:bg-neutral-900 sticky top-0 z-10">
                                <div>
                                    <h3 className="text-lg font-bold text-neutral-800 dark:text-neutral-100">
                                        {t('modals.shareJournal.title', 'Share "{{name}}"', { name: journal.name })}
                                    </h3>
                                </div>
                                <button
                                    onClick={handleClose}
                                    className="p-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                                >
                                    <span className="w-5 h-5" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_2e46f7c6.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_2e46f7c6.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                </button>
                            </div>

                            <div className="p-6 overflow-y-auto flex-1">


                                {isLoading ? (
                                    <div className="py-12 flex flex-col items-center justify-center text-neutral-400 gap-3">
                                        <div className="w-8 h-8 border-2 border-neutral-200 border-t-accent-primary rounded-full animate-spin" />
                                        <p className="text-xs">{t('common.loading', 'Loading...')}</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {shares.map((share, index) => (
                                            <div key={index} className="flex gap-2 items-center">
                                                {/* Email Field */}
                                                <div className="flex-1">
                                                    <input
                                                        ref={index === shares.length - 1 ? lastInputRef : null}
                                                        type="email"
                                                        value={share.email}
                                                        onChange={(e) => handleUpdateRow(index, 'email', e.target.value)}
                                                        placeholder={t('modals.shareJournal.emailLabel', 'Email address') as string}
                                                        className={`w-full px-4 py-2.5 bg-neutral-50 dark:bg-neutral-800 border rounded-xl text-sm outline-none transition-all text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 ${share.error
                                                            ? 'border-red-500 ring-2 ring-red-500/20 bg-red-50 dark:bg-red-900/10'
                                                            : 'border-neutral-200 dark:border-neutral-700 focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary'
                                                            }`}
                                                    />
                                                </div>

                                                {/* Permission Selector */}
                                                <div className="relative">
                                                    <select
                                                        value={share.permission}
                                                        onChange={(e) => handleUpdateRow(index, 'permission', e.target.value)}
                                                        className="appearance-none h-[42px] w-32 pl-3 pr-8 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl text-xs font-medium text-neutral-600 dark:text-neutral-300 outline-none focus:border-accent-primary focus:ring-1 focus:ring-accent-primary/20 transition-all cursor-pointer"
                                                    >
                                                        <option value="read">{t('sharing.readOnly', 'Read only')}</option>
                                                        <option value="readwrite">{t('sharing.readWrite', 'Read & Write')}</option>
                                                    </select>
                                                    <span className="w-4 h-4 absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_c03edf15.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_c03edf15.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                                </div>

                                                {/* Remove Button */}
                                                <button
                                                    onClick={() => handleRemoveRow(index)}
                                                    className="p-2 h-[42px] w-[42px] flex items-center justify-center text-neutral-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all border border-transparent hover:border-red-100 dark:hover:border-red-900/30 flex-shrink-0"
                                                    title={t('common.remove', 'Remove') as string}
                                                >
                                                    <span className="w-5 h-5" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_b0012a36.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_b0012a36.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                                </button>
                                            </div>
                                        ))}

                                        <button
                                            onClick={handleAddRow}
                                            className="mt-2 text-sm font-medium text-accent-primary hover:text-accent-primary/80 flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-accent-primary/5 dark:hover:bg-accent-primary/10 transition-colors w-fit"
                                        >
                                            <span className="w-4 h-4" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_1af3a04d.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_1af3a04d.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                            {t('sharing.addAnother', 'Add another person')}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="p-6 border-t border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 flex gap-3">
                                <button
                                    onClick={handleClose}
                                    className="flex-1 py-3 text-neutral-600 dark:text-neutral-300 font-semibold rounded-xl hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                                >
                                    {t('common.cancel', 'Cancel')}
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={isSaving || isLoading}
                                    className="flex-1 py-3 bg-accent-primary text-white font-bold rounded-xl hover:bg-accent-primary/90 shadow-lg shadow-accent-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isSaving ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            {t('common.saving', 'Saving...')}
                                        </>
                                    ) : (
                                        t('common.save', 'Save Changes')
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};
